// src/app/features/profile/components/phone-number-input/phone-number-input.component.ts
import { TranslateService } from '@ngx-translate/core';
import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  forwardRef
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { v4 as uuidv4 } from 'uuid';
import { Subject, take, takeUntil } from 'rxjs';
import { PhonePrefix, PhonePrefixService } from './service/phone-prefix.service';
import parsePhoneNumberFromString from 'libphonenumber-js';
import { MyAccountService } from 'src/app/core/services/my-account.service';
import { isTaiwanCountry, normalizeCountryValue } from 'src/app/core/utils/country.utils';
import { UserRoleService } from 'src/app/core/services/role.service';

import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

interface UserCountrySource {
  country?: string | null;
}

@Component({
  selector: 'app-phone-number-input',
  templateUrl: './phone-number-input.component.html',
  styleUrls: ['./phone-number-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneNumberInputComponent),
      multi: true,
    },
  ],
})
export class PhoneNumberInputComponent implements OnChanges, OnDestroy, ControlValueAccessor {
  // External input (backwards-compatible) — if parent uses [phoneNumber]
  @Input() phoneNumber: string | null | undefined = '';
  @Input() defaultPrefix: PhonePrefix | null = null;
  @Input() countryFromSettings: string | null = null;
  @Input() required: boolean = true;
  @Input() strictCountryValidation: boolean = false;
  @Input() forceShowErrors: boolean = false;

  @Output() phoneChange = new EventEmitter<string>();
  @Output() countryChange = new EventEmitter<string>();

  prefixes: PhonePrefix[] = [];
  favoritePrefixes: PhonePrefix[] = [];
  uniqueId: string = uuidv4();

  selectedPrefix: PhonePrefix | undefined;
  localNumber = new FormControl('');
  dropdownOpen = false;

  private readonly FAVORITE_COUNTRY_CODES = ['ES', 'PT', 'FR', 'GB', 'DE', 'IT', 'US', 'CR', 'TW'] as const;
  private readonly COUNTRY_EXAMPLE_BY_CODE: Record<string, string> = {
    ES: '612345678',
    FR: '612345678',
    GB: '7123456789',
    IT: '3123456789',
    DE: '15123456789',
    PT: '912345678',
    US: '5551234567',
    CL: '912345678',
    CR: '61234567',
    TW: '9123456789',
  };

  private readonly LENGTH_RULES_BY_COUNTRY: Record<string, { min: number; max: number }> = {
    // Países activos en plataforma (acotación amplia para cubrir más casos nacionales)
    ES: { min: 9, max: 9 },
    FR: { min: 9, max: 9 },
    GB: { min: 7, max: 10 },
    IT: { min: 6, max: 12 },
    DE: { min: 4, max: 15 },
    US: { min: 10, max: 10 },
    CL: { min: 9, max: 11 },
    CR: { min: 8, max: 10 },
    TW: { min: 10, max: 10 },
    // Compatibilidad histórica
    PT: { min: 9, max: 9 }
  };

  private readonly DEFAULT_MAX_LENGTH = 15;
  private readonly MIN_VALIDATION_RATIO = 0.6;
  private hasInternalFallbackPrefix = false;

  private validationMessage: string | null = null;
  private helperMessage: string | null = null;
  private destroy$ = new Subject<void>();

  // ControlValueAccessor callbacks
  private onChange: (value: string) => void = () => { };
  private onTouched: () => void = () => { };
  private isDisabled = false;

  constructor(
    private phonePrefixService: PhonePrefixService,
    private translateService: TranslateService,
    private myAccountService: MyAccountService,
    private userRoleService: UserRoleService
  ) {
    this.prefixes = this.phonePrefixService.getPhonePrefixesSorted();
    this.favoritePrefixes = this.prefixes.filter(p => this.FAVORITE_COUNTRY_CODES.includes(p.code as typeof this.FAVORITE_COUNTRY_CODES[number]));

    if (!this.defaultPrefix) {
      this.defaultPrefix = this.phonePrefixService.getPrefixByCode('ES') ?? this.prefixes[0] ?? null;
      this.hasInternalFallbackPrefix = true;
    }
    this.selectedPrefix = this.defaultPrefix ?? this.prefixes[0];
    this.setHelperMessage();

    this.initializeDefaultPrefixFromSettings();

    this.localNumber.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(raw => this.handleLocalValueChange(raw));

    this.translateService.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.prefixes = this.phonePrefixService.getPhonePrefixesSorted();
        this.favoritePrefixes = this.prefixes.filter(p => this.FAVORITE_COUNTRY_CODES.includes(p.code as typeof this.FAVORITE_COUNTRY_CODES[number]));

        if (this.selectedPrefix?.code) {
          const translatedSelectedPrefix = this.phonePrefixService.getPrefixByCode(this.selectedPrefix.code);
          if (translatedSelectedPrefix) {
            this.selectedPrefix = translatedSelectedPrefix;
          }
        }

        if (this.defaultPrefix?.code) {
          const translatedDefaultPrefix = this.phonePrefixService.getPrefixByCode(this.defaultPrefix.code);
          if (translatedDefaultPrefix) {
            this.defaultPrefix = translatedDefaultPrefix;
          }
        }

        this.refreshDisplayedMessages();
      });
  }

  private initializeDefaultPrefixFromSettings(): void {
    if (this.defaultPrefix && !this.hasInternalFallbackPrefix) {
      this.selectedPrefix = this.defaultPrefix;
      return;
    }

    const hasExistingPhone = !!(this.phoneNumber && this.phoneNumber.toString().trim().length > 0);
    if (hasExistingPhone) {
      return;
    }

    if (this.applyCountryAsDefaultPrefix(this.countryFromSettings)) {
      this.setHelperMessage();
      return;
    }

    if (this.applyCountryAsDefaultPrefix(this.getUserCountryFromStorage())) {
      this.setHelperMessage();
      return;
    }

    this.userRoleService.getCurrentUser()
      .pipe(take(1))
      .subscribe({
        next: (user: UserCountrySource | null | undefined) => {
          if (this.applyCountryAsDefaultPrefix(user?.country ?? null)) {
            this.setHelperMessage();
            return;
          }

          this.loadPrefixFromCenterFallback();
        },
        error: () => {
          this.loadPrefixFromCenterFallback();
        }
      });
  }

  private loadPrefixFromCenterFallback(): void {

    this.myAccountService.getCenterInfo()
      .pipe(take(1))
      .subscribe({
        next: (center: UserCountrySource | null | undefined) => {
          if (this.applyCountryAsDefaultPrefix(center?.country ?? null)) {
            this.setHelperMessage();
            return;
          }

          if (isTaiwanCountry(center?.country)) {
            const taiwanPrefix = this.phonePrefixService.getPrefixByCode('TW');
            if (taiwanPrefix) {
              this.defaultPrefix = taiwanPrefix;
              this.selectedPrefix = taiwanPrefix;
              this.setHelperMessage();
            }
          }
        },
        error: () => {
          // Mantener comportamiento por defecto si falla la carga del centro
        }
      });
  }

  private applyCountryAsDefaultPrefix(country: string | null | undefined): boolean {
    const code = this.resolvePrefixCountryCode(country);
    if (!code) {
      return false;
    }

    const prefix = this.phonePrefixService.getPrefixByCode(code);
    if (!prefix) {
      return false;
    }

    this.defaultPrefix = prefix;
    this.selectedPrefix = prefix;
    this.hasInternalFallbackPrefix = false;
    return true;
  }

  private resolvePrefixCountryCode(country: string | null | undefined): string | null {
    if (!country) {
      return null;
    }

    const normalized = normalizeCountryValue(country);
    if (!normalized) {
      return null;
    }

    const aliasToCode: Record<string, string> = {
      UK: 'GB',
      'UNITED KINGDOM': 'GB',
      'REINO UNIDO': 'GB',
      REINOUNIDO: 'GB',
      USA: 'US',
      'UNITED STATES': 'US',
      'ESTADOS UNIDOS': 'US',
      ESTADOSUNIDOS: 'US',
      TAIWAN: 'TW',
      ESPANA: 'ES',
      SPAIN: 'ES',
      FRANCE: 'FR',
      FRANCIA: 'FR',
      ITALY: 'IT',
      ITALIA: 'IT',
      PORTUGAL: 'PT',
      GERMANY: 'DE',
      ALEMANIA: 'DE',
      NETHERLANDS: 'NL',
      'PAISES BAJOS': 'NL',
      CHILE: 'CL',
      'COSTA RICA': 'CR',
      COSTARICA: 'CR',
      COSTRICA: 'CR',
      ANDORRA: 'AD'
    };

    const candidateCode = aliasToCode[normalized] ?? normalized;
    return this.phonePrefixService.getPrefixByCode(candidateCode) ? candidateCode : null;
  }

  private getUserCountryFromStorage(): string | null {
    try {
      const userRaw = localStorage.getItem('user');
      if (!userRaw) {
        return null;
      }

      const parsed = JSON.parse(userRaw);
      return parsed?.country ?? null;
    } catch {
      return null;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    const phoneChange = changes['phoneNumber'];
    if (phoneChange) {
      const currentValue = phoneChange.currentValue ?? undefined;
      const isFirst = phoneChange.isFirstChange();
      this.initializePhoneData(currentValue, !isFirst);
    }

    const prefixChange = changes['defaultPrefix'];
    if (prefixChange?.currentValue) {
      const nextPrefix = prefixChange.currentValue as PhonePrefix;
      const prevPrefix = prefixChange.previousValue as PhonePrefix | undefined;
      const hasLocal = (this.localNumber.value ?? '').toString().trim().length > 0;
      this.hasInternalFallbackPrefix = false;

      if (!hasLocal && (!this.selectedPrefix || (prevPrefix && this.selectedPrefix.code === prevPrefix.code))) {
        this.selectedPrefix = nextPrefix;
        this.emitCountryChange();
        this.setHelperMessage();
      }
    }

    const countryFromSettingsChange = changes['countryFromSettings'];
    if (countryFromSettingsChange) {
      const hasPhoneInput = !!(this.phoneNumber && this.phoneNumber.toString().trim().length > 0);
      const hasLocal = (this.localNumber.value ?? '').toString().trim().length > 0;

      if (!hasPhoneInput && !hasLocal) {
        this.applyCountryAsDefaultPrefix(countryFromSettingsChange.currentValue ?? null);
        this.setHelperMessage();
      }

      this.validateLocalNumber((this.localNumber.value ?? '').toString());
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ---------- Helpers ----------
  private sanitizeDigits(input: string): string {
    return (input || '').replace(/[^\d]/g, '');
  }

  private initializePhoneData(fullPhone?: string | null, emitChange = false, validate = true): void {
    if (!this.prefixes?.length) return;

    const sanitizedPhone = (fullPhone ?? '').replace(/\s+/g, '');

    const matched = this.prefixes.find(p => sanitizedPhone.startsWith(p.prefix));
    if (matched) {
      this.selectedPrefix = matched;
      const local = sanitizedPhone.replace(matched.prefix, '');
      const cleaned = this.sanitizeDigits(local);
      const maxLocal = this.getMaxLocalLengthForSelectedPrefix();
      this.localNumber.setValue(cleaned.slice(0, maxLocal), { emitEvent: false });
    } else {
      this.selectedPrefix = this.defaultPrefix ?? this.prefixes[0];
      const cleaned = this.sanitizeDigits(sanitizedPhone);
      const maxLocal = this.getMaxLocalLengthForSelectedPrefix();
      this.localNumber.setValue(cleaned.slice(0, maxLocal), { emitEvent: false });
    }

    if (emitChange) {
      this.emitPhoneNumber();
    }

    if (validate) {
      this.refreshDisplayedMessages();
    }
  }

  toggleDropdown() {
    if (this.isDisabled) return;
    this.dropdownOpen = !this.dropdownOpen;
  }

  onPrefixChange(p: PhonePrefix | undefined) {
    if (this.isDisabled) return;
    this.selectedPrefix = p;
    this.dropdownOpen = false;
    this.onTouched();
    this.emitCountryChange();

    const current = String(this.localNumber.value ?? '');
    const limited = current.slice(0, this.getMaxLocalLengthForSelectedPrefix());
    if (current !== limited) {
      this.localNumber.setValue(limited);
    } else {
      this.validateLocalNumber(current);
      this.emitPhoneNumber();
    }
  }

  private handleLocalValueChange(rawValue: any) {
    const raw = (rawValue ?? '').toString();

    const cleaned = this.sanitizeDigits(raw);

    const maxLocal = this.getMaxLocalLengthForSelectedPrefix();
    const limited = cleaned.slice(0, maxLocal);

    if (limited !== raw) {
      this.localNumber.setValue(limited, { emitEvent: false });
    }

    this.validateLocalNumber(limited);
    this.emitPhoneNumber();
  }

  public getMaxLocalLengthForSelectedPrefix(): number {
    if (!this.selectedPrefix?.code) return this.DEFAULT_MAX_LENGTH;

    const rule = this.LENGTH_RULES_BY_COUNTRY[this.selectedPrefix.code];
    return rule?.max ?? this.DEFAULT_MAX_LENGTH;
  }

  public getMinLocalLengthForSelectedPrefix(): number {
    if (!this.selectedPrefix?.code) {
      const max = this.getMaxLocalLengthForSelectedPrefix();
      return Math.max(6, Math.ceil(max * this.MIN_VALIDATION_RATIO));
    }

    const rule = this.LENGTH_RULES_BY_COUNTRY[this.selectedPrefix.code];
    return rule?.min ?? Math.max(6, Math.ceil(this.getMaxLocalLengthForSelectedPrefix() * this.MIN_VALIDATION_RATIO));
  }

  private getCountryExampleForSelectedPrefix(): string {
    if (!this.selectedPrefix?.code) {
      return '000000000';
    }
    return this.COUNTRY_EXAMPLE_BY_CODE[this.selectedPrefix.code] ?? '000000000';
  }

  getLocalNumberPlaceholder(): string {
    if (this.selectedPrefix?.code === 'TW') {
      return '09** *** ***';
    }
    return this.formatLocalForDisplay(this.getCountryExampleForSelectedPrefix());
  }

  getRootAriaLabel(): string {
    return this.translateService.instant('phone_number_input.aria_root') || 'Phone number input';
  }

  getInputAriaLabel(): string {
    return this.translateService.instant('phone_number_input.aria_local_number') || 'Phone local number';
  }

  private validateLocalNumber(local: string) {
    const control = this.localNumber;
    const cleaned = this.sanitizeDigits(local ?? '');
    const country = this.selectedPrefix?.code ?? undefined;

    const max = this.getMaxLocalLengthForSelectedPrefix();
    const min = this.getMinLocalLengthForSelectedPrefix();

    if (!cleaned.length) {
      if (this.required) {
        this.validationMessage = this.translateService.instant('phone_number_input.validation_required') || 'El número es obligatorio.';
        control.setErrors({ required: true });
        this.setHelperMessage();
        return;
      }

      this.validationMessage = null;
      control.setErrors(null);
      this.setHelperMessage();
      return;
    }

    if (cleaned.length < min) {
      this.validationMessage = this.translateService.instant('phone_number_input.validation_too_short', {
        min,
        max,
        prefix: this.selectedPrefix?.prefix ?? '',
        country: this.selectedPrefix?.nameTranslated ?? this.selectedPrefix?.code ?? ''
      });
      control.setErrors({ minlength: true });
      return;
    }

    if (cleaned.length > max) {
      this.validationMessage = this.translateService.instant('phone_number_input.validation_too_long', {
        min,
        max,
        prefix: this.selectedPrefix?.prefix ?? '',
        country: this.selectedPrefix?.nameTranslated ?? this.selectedPrefix?.code ?? ''
      });
      control.setErrors({ maxlength: true });
      return;
    }

    const isValidByLibrary = this.validatePhoneWithLibrary(cleaned, country);
    if (!isValidByLibrary && this.strictCountryValidation) {
      this.validationMessage = this.translateService.instant('phone_number_input.validation_invalid', {
        prefix: this.selectedPrefix?.prefix ?? '',
        country: this.selectedPrefix?.nameTranslated ?? this.selectedPrefix?.code ?? ''
      });
      control.setErrors({ invalidPhone: true });
      return;
    }

    this.validationMessage = null;
    control.setErrors(null);
    this.setHelperMessage();
  }

  private refreshDisplayedMessages(): void {
    this.validateLocalNumber((this.localNumber.value ?? '').toString());
  }

  private setHelperMessage(): void {
    this.helperMessage = null;
  }

  private validatePhoneWithLibrary(cleaned: string, country?: string): boolean {
    try {
      const candidateE164 = `${this.selectedPrefix?.prefix || ''}${cleaned}`;
      let parsed = parsePhoneNumberFromString(candidateE164, country as any);

      if (!parsed) {
        parsed = parsePhoneNumberFromString(cleaned, country as any);
      }

      if (!parsed && country && cleaned.startsWith('0')) {
        parsed = parsePhoneNumberFromString(cleaned.replace(/^0+/, ''), country as any);
      }

      return parsed?.isPossible() || parsed?.isValid() || false;
    } catch (error) {
      // Mantener flujo permisivo en caso de error de librería para no romper formularios existentes.
      return true;
    }
  }

  getErrorMessage(): string | null {
    return this.validationMessage;
  }

  getHelperOrErrorMessage(): string | null {
    return this.shouldShowValidationMessage() ? this.validationMessage : null;
  }

  hasErrorMessage(): boolean {
    return this.shouldShowValidationMessage() && !!this.validationMessage;
  }

  private shouldShowValidationMessage(): boolean {
    return this.forceShowErrors || this.localNumber.touched || this.localNumber.dirty;
  }

  private emitPhoneNumber() {
    const local = (this.localNumber.value ?? '').toString().replace(/\s+/g, '');
    if (!local) {
      this.onChange('');
      this.phoneChange.emit('');
      return;
    }
    const fullPhone = `${this.selectedPrefix?.prefix || ''}${local}`;
    this.onChange(fullPhone);
    this.phoneChange.emit(fullPhone);
  }

  private emitCountryChange(): void {
    this.countryChange.emit(this.selectedPrefix?.code ?? '');
  }

  formatLocalForDisplay(localVal: string | null | undefined): string {
    const digits = this.sanitizeDigits(localVal ?? '');
    return digits.replace(/(\d{3})(?=\d)/g, '$1 ').trim();
  }

  trackByCode(_index: number, item: PhonePrefix) {
    return item.code;
  }

  onPrefixKeydown(event: KeyboardEvent, prefix: PhonePrefix): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onPrefixChange(prefix);
    }
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    const targetElement = event.target as HTMLElement;
    const element = document.getElementById(`${this.uniqueId}`);
    if (element && !element.contains(targetElement)) {
      this.dropdownOpen = false;
    }
  }

  writeValue(obj: string | null): void {
    const value = obj ?? '';
    this.phoneNumber = value;
    this.initializePhoneData(value, false, false);
    setTimeout(() => this.refreshDisplayedMessages(), 0);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  markTouched(): void {
    this.onTouched();
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = !!isDisabled;
    if (this.isDisabled) {
      this.localNumber.disable({ emitEvent: false });
    } else {
      this.localNumber.enable({ emitEvent: false });
    }
  }
}
